import { upvoteCall as apiUpvoteCall, downvoteCall as apiDownvoteCall, fetchVote } from '../bff'
import logger from '../../utils/logger'

/**
 * Handle upvoting a call and update the related state
 * @param {string} callId - The ID of the call to upvote
 * @param {string} userId - The ID of the current user
 * @param {Function} updateVoteState - Callback to update vote state (receives callId and 'up')
 * @param {Function} refreshMetrics - Callback to refresh metrics after voting
 * @returns {Promise<boolean>} Success status
 */
export const handleUpvote = async (callId, userId, updateVoteState, refreshMetrics) => {
  if (!callId) {
    logger.error('No call ID available for upvote.')
    return false
  }

  try {
    const success = await apiUpvoteCall(callId, userId)

    if (success) {
      if (updateVoteState) {
        updateVoteState(callId, 'up')
      }

      if (refreshMetrics) {
        await refreshMetrics()
      }

      return true
    } else {
      logger.error('Upvote failed.')
      return false
    }
  } catch (error) {
    logger.error('Error during upvote:', error)
    return false
  }
}

/**
 * Handle downvoting a call and update the related state
 * @param {string} callId - The ID of the call to downvote
 * @param {string} userId - The ID of the current user
 * @param {Function} updateVoteState - Callback to update vote state (receives callId and 'down')
 * @param {Function} refreshMetrics - Callback to refresh metrics after voting
 * @returns {Promise<boolean>} Success status
 */
export const handleDownvote = async (callId, userId, updateVoteState, refreshMetrics) => {
  if (!callId) {
    logger.error('No call ID available for downvote.')
    return false
  }

  try {
    const success = await apiDownvoteCall(callId, userId)

    if (success) {
      if (updateVoteState) {
        updateVoteState(callId, 'down')
      }

      if (refreshMetrics) {
        await refreshMetrics()
      }

      return true
    } else {
      logger.error('Downvote failed.')
      return false
    }
  } catch (error) {
    logger.error('Error during downvote:', error)
    return false
  }
}

/**
 * Get the vote state for a single call
 * @param {string} callId - The ID of the call
 * @param {string} userId - The ID of the current user
 * @returns {Promise<string|null>} 'up', 'down', or null
 */
export const getVoteState = async (callId, userId) => {
  try {
    const data = await fetchVote(callId, userId)
    return data.vote === 1 ? 'up' : data.vote === -1 ? 'down' : null
  } catch (error) {
    logger.error('Error fetching vote state:', error)
    return null
  }
}

/**
 * Get the vote states for multiple calls
 * @param {Array} callIds - Array of call IDs
 * @param {string} userId - The ID of the current user
 * @returns {Promise<Object>} Object mapping call IDs to vote states
 */
export const getVoteStates = async (callIds, userId) => {
  if (!callIds || callIds.length === 0 || !userId) {
    return {}
  }

  const votes = {}

  await Promise.all(
    callIds.map(async (callId) => {
      if (!callId) return

      try {
        const data = await fetchVote(callId, userId)
        votes[callId] = data.vote === 1 ? 'up' : data.vote === -1 ? 'down' : null
      } catch (error) {
        votes[callId] = null
      }
    })
  )

  return votes
}
